import { useState } from 'react';
import { Link, useParams } from '@tanstack/react-router';
import { useApiClient } from '@rudi/api/react';
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { Button } from '@rudi/ui/button';
import { Input } from '@rudi/ui/input';
import { RolePicker, type RoleCategory } from '@rudi/ui/role-picker';
import { toast } from 'sonner';

interface Role {
  id: string;
  name: string;
  displayName: string;
  description: string;
  permissions: string[];
  unsupportedPermissions: string[];
  isActive: boolean;
  isBuiltin: boolean;
  protected: boolean;
  category?: RoleCategory;
}
interface Permission {
  key: string;
  label: string;
  application: string;
  description: string;
  scope: string;
  assignable: boolean;
}
interface Member {
  userId: string;
  name: string;
  email: string;
  roleIds: string[];
  roles: Role[];
  effectivePermissions: Record<string, string[]>;
}
interface RoleData {
  roles: Role[];
  permissions: Permission[];
  defaultRoleId: string | null;
}
const emptyDraft = { name: '', displayName: '', description: '', permissions: [] as string[] };
function errorMessage(error: unknown): string {
  const detail = (error as { response?: { data?: { detail?: unknown } } }).response?.data?.detail;
  return typeof detail === 'string' ? detail : 'Could not save the access settings.';
}

export function TenantAccessPage() {
  const { tenantId } = useParams({ strict: false }) as { tenantId: string };
  const api = useApiClient();
  const qc = useQueryClient();
  const base = `/access/tenants/${tenantId}`;
  const catalog = useQuery({
    queryKey: ['tenant-access', tenantId, 'roles'],
    queryFn: async () => (await api.get<RoleData>(`${base}/roles`)).data,
  });
  const members = useQuery({
    queryKey: ['tenant-access', tenantId, 'members'],
    queryFn: async () => (await api.get<Member[]>(`${base}/members`)).data,
  });
  const [editing, setEditing] = useState<string | null>(null);
  const [showEditor, setShowEditor] = useState(false);
  const [draft, setDraft] = useState(emptyDraft);
  const [selectedUser, setSelectedUser] = useState<string | null>(null);
  const [selectedRoles, setSelectedRoles] = useState<string[]>([]);
  const mutation = useMutation({
    mutationFn: async (action: () => Promise<unknown>) => action(),
    onSuccess: async () => {
      await qc.invalidateQueries({ queryKey: ['tenant-access', tenantId] });
      await qc.invalidateQueries({ queryKey: ['tenant-members', tenantId] });
      setShowEditor(false);
      setSelectedUser(null);
      toast.success('Access settings saved');
    },
    onError: error => toast.error(errorMessage(error)),
  });
  const saveRole = () =>
    mutation.mutate(() =>
      editing
        ? api.patch(`${base}/roles/${editing}`, {
            displayName: draft.displayName,
            description: draft.description,
            permissions: draft.permissions,
          })
        : api.post(`${base}/roles`, draft)
    );
  const permissions = catalog.data?.permissions.filter(p => p.assignable) ?? [];
  const applications = [...new Set(permissions.map(p => p.application))];
  return (
    <main className="mx-auto max-w-6xl space-y-6 p-6">
      <Link to="/tenants/$tenantId" params={{ tenantId }}>
        Back to tenant
      </Link>
      <header className="flex items-center justify-between">
        <div>
          <h1 className="text-2xl font-semibold">Roles and permissions</h1>
          <p className="text-sm text-muted-foreground">
            Assign several roles to combine their permissions. Tenant and team boundaries still
            apply.
          </p>
        </div>
        <Button
          onClick={() => {
            setEditing(null);
            setDraft(emptyDraft);
            setShowEditor(true);
          }}
        >
          Create role
        </Button>
      </header>
      {(catalog.error || members.error) && (
        <p role="alert">
          Could not load access settings.{' '}
          <Button
            variant="outline"
            onClick={() => {
              void catalog.refetch();
              void members.refetch();
            }}
          >
            Retry
          </Button>
        </p>
      )}
      {catalog.isLoading && <p>Loading roles…</p>}
      {catalog.data && (
        <label className="block space-y-2">
          Signup default
          <select
            aria-label="Signup default"
            className="ml-3 rounded-3xl border p-2"
            value={catalog.data.defaultRoleId ?? ''}
            disabled={mutation.isPending}
            onChange={e =>
              mutation.mutate(() => api.put(`${base}/default-role`, { roleId: e.target.value }))
            }
          >
            <option value="" disabled>
              Select a role
            </option>
            {catalog.data.roles
              .filter(
                r =>
                  r.isActive &&
                  !r.protected &&
                  !r.permissions.some(p =>
                    catalog.data?.permissions.some(d => d.key === p && !d.assignable)
                  )
              )
              .map(r => (
                <option key={r.id} value={r.id}>
                  {r.displayName}
                </option>
              ))}
          </select>
        </label>
      )}
      <div className="grid gap-3 md:grid-cols-2">
        {catalog.data?.roles.map(role => (
          <section key={role.id} className="space-y-3 rounded-3xl border p-4">
            <h2 className="font-medium">
              {role.displayName}
              {!role.isActive && ' · Inactive'}
            </h2>
            <p className="text-sm text-muted-foreground">{role.description}</p>
            <p className="text-sm">
              {role.permissions.length}{' '}
              {role.permissions.length === 1 ? 'permission' : 'permissions'} ·{' '}
              {role.protected ? 'Protected' : role.isBuiltin ? 'Default' : 'Custom'}
            </p>
            {role.unsupportedPermissions.length > 0 && (
              <p role="status" className="text-sm">
                Unsupported legacy permissions grant no access:{' '}
                {role.unsupportedPermissions.join(', ')}
              </p>
            )}
            <div className="flex flex-wrap gap-2">
              {!role.protected && (
                <Button
                  variant="outline"
                  onClick={() => {
                    setEditing(role.id);
                    setDraft({
                      name: role.name,
                      displayName: role.displayName,
                      description: role.description ?? '',
                      permissions: role.permissions,
                    });
                    setShowEditor(true);
                  }}
                >
                  Edit {role.displayName}
                </Button>
              )}
              <Button
                variant="outline"
                onClick={() => {
                  setEditing(null);
                  setDraft({
                    name: `${role.name}_copy`,
                    displayName: `${role.displayName} copy`,
                    description: role.description ?? '',
                    permissions: role.permissions.filter(p => permissions.some(d => d.key === p)),
                  });
                  setShowEditor(true);
                }}
              >
                Duplicate
              </Button>
              {!role.protected && (
                <Button
                  variant="outline"
                  disabled={mutation.isPending || role.id === catalog.data?.defaultRoleId}
                  onClick={() =>
                    mutation.mutate(() =>
                      api.patch(`${base}/roles/${role.id}`, { isActive: !role.isActive })
                    )
                  }
                >
                  {role.isActive ? 'Deactivate' : 'Reactivate'}
                </Button>
              )}
            </div>
          </section>
        ))}
      </div>
      {showEditor && (
        <section className="space-y-4 rounded-3xl border p-5" aria-label="Role editor">
          <h2 className="text-lg font-medium">{editing ? 'Edit role' : 'Create role'}</h2>
          {!editing && (
            <label className="block">
              Role identifier
              <Input
                aria-label="Role identifier"
                value={draft.name}
                onChange={e => setDraft({ ...draft, name: e.target.value })}
                placeholder="research_coordinator"
              />
            </label>
          )}
          <label className="block">
            Role name
            <Input
              aria-label="Role name"
              value={draft.displayName}
              onChange={e => setDraft({ ...draft, displayName: e.target.value })}
            />
          </label>
          <label className="block">
            Description
            <Input
              aria-label="Role description"
              value={draft.description}
              onChange={e => setDraft({ ...draft, description: e.target.value })}
            />
          </label>
          {applications.map(app => (
            <fieldset key={app} className="space-y-2 rounded-3xl border p-3">
              <legend className="px-2 capitalize">{app.replaceAll('_', ' ')}</legend>
              {permissions
                .filter(p => p.application === app)
                .map(p => (
                  <label key={p.key} className="flex items-start gap-3 text-sm">
                    <input
                      type="checkbox"
                      aria-label={`${p.application.replaceAll('_', ' ')}: ${p.label}`}
                      checked={draft.permissions.includes(p.key)}
                      onChange={e =>
                        setDraft({
                          ...draft,
                          permissions: e.target.checked
                            ? [...draft.permissions, p.key]
                            : draft.permissions.filter(key => key !== p.key),
                        })
                      }
                    />
                    <span>
                      {p.label}
                      <span className="block text-xs text-muted-foreground">
                        {p.description} Scope: {p.scope}.
                      </span>
                    </span>
                  </label>
                ))}
            </fieldset>
          ))}
          <div className="flex gap-2">
            <Button
              disabled={
                mutation.isPending || !draft.displayName.trim() || (!editing && !draft.name.trim())
              }
              onClick={saveRole}
            >
              Save role
            </Button>
            <Button variant="outline" onClick={() => setShowEditor(false)}>
              Cancel
            </Button>
          </div>
        </section>
      )}
      <section className="space-y-3">
        <h2 className="text-lg font-medium">Member access</h2>
        {members.data?.map(member => (
          <article key={member.userId} className="space-y-3 rounded-3xl border p-4">
            <div className="flex items-center justify-between">
              <div>
                <h3 className="font-medium">{member.name}</h3>
                <p className="text-sm text-muted-foreground">{member.email}</p>
              </div>
              <Button
                variant="outline"
                onClick={() => {
                  setSelectedUser(member.userId);
                  setSelectedRoles(member.roleIds);
                }}
              >
                Assign roles to {member.name}
              </Button>
            </div>
            <p>
              {member.roles
                .map(r => `${r.displayName}${r.isActive ? '' : ' · Inactive'}`)
                .join(', ') || 'No roles'}
            </p>
            <details>
              <summary>Effective permissions</summary>
              <ul className="mt-2 space-y-1 text-sm">
                {Object.entries(member.effectivePermissions).map(([permission, sources]) => (
                  <li key={permission}>
                    {permission} · {sources.join(', ')}
                  </li>
                ))}
              </ul>
            </details>
            {selectedUser === member.userId && (
              <div className="space-y-2">
                <RolePicker
                  label={`Roles for ${member.name}`}
                  roles={(catalog.data?.roles ?? []).filter(
                    r => r.isActive && r.name !== 'team_admin'
                  )}
                  value={selectedRoles}
                  onChange={setSelectedRoles}
                  disabled={mutation.isPending}
                />
                <Button
                  disabled={mutation.isPending || selectedRoles.length === 0}
                  onClick={() =>
                    mutation.mutate(() =>
                      api.put(`${base}/members/${member.userId}/roles`, { roleIds: selectedRoles })
                    )
                  }
                >
                  Save assignments
                </Button>
              </div>
            )}
          </article>
        ))}
      </section>
    </main>
  );
}
